import {
  DefaultError,
  keepPreviousData,
  QueryKey,
  useIsFetching,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import { createContext, useContext, useState } from "react"

// Under this context queries keep their previous data and swap to the new one all at once
export const DashboardSyncContext = createContext(false)

export function useDashboardQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const isSynced = useContext(DashboardSyncContext)
  const isSyncing = useIsDashboardSyncing()

  const result = useQuery({
    ...options,
    ...(isSynced && {
      placeholderData: keepPreviousData,
      meta: { ...options.meta, isDashboardSynced: true },
    }),
  })

  const [syncedData, setSyncedData] = useState(result.data)

  if (isSynced && !isSyncing && !result.isPlaceholderData && syncedData !== result.data) {
    setSyncedData(result.data)
  }

  const shouldHoldData = isSynced && isSyncing && syncedData !== undefined

  return { ...result, data: shouldHoldData ? syncedData : result.data }
}

export function useIsDashboardSyncing() {
  return useIsFetching({ predicate: (query) => query.meta?.isDashboardSynced === true }) > 0
}
